/** 신규 사용자 생성 모달 (M12 / U3-c). */

import { useEffect, useState } from "react";
import { Loader2, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { UserRole } from "@/lib/api/admin_users";
import { ApiError } from "@/lib/api/client";

import { useCreateAdminUser } from "./useAdminUsers";

const PASSWORD_MIN_LEN = 4;

interface Props {
  onClose: () => void;
}

export default function CreateUserModal({ onClose }: Props) {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<UserRole>("user");

  const mutation = useCreateAdminUser();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const valid = email.includes("@") && password.length >= PASSWORD_MIN_LEN;

  const apiError =
    mutation.error instanceof ApiError ? mutation.error.detail : (mutation.error?.message ?? null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid || mutation.isPending) return;
    mutation.mutate(
      { email, password, role },
      {
        onSuccess: () => {
          onClose();
        },
      },
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4 py-8"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <h2 className="text-sm font-semibold text-slate-900">{t("admin.users.create.title")}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
            aria-label={t("common.actions.close")}
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <form onSubmit={handleSubmit} className="space-y-4 px-5 py-4">
          <div>
            <label htmlFor="cu-email" className="block text-xs font-medium text-slate-700">
              {t("admin.users.create.emailLabel")}
            </label>
            <input
              id="cu-email"
              type="email"
              autoComplete="off"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="user@example.com"
              disabled={mutation.isPending}
            />
          </div>

          <div>
            <label htmlFor="cu-pw" className="block text-xs font-medium text-slate-700">
              {t("admin.users.create.passwordLabel", { count: PASSWORD_MIN_LEN })}
            </label>
            <input
              id="cu-pw"
              type="password"
              autoComplete="new-password"
              required
              minLength={PASSWORD_MIN_LEN}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              disabled={mutation.isPending}
            />
            <p className="mt-1 text-[11px] text-slate-400">
              {t("admin.users.create.passwordHint")}
            </p>
          </div>

          <div>
            <label htmlFor="cu-role" className="block text-xs font-medium text-slate-700">
              {t("admin.users.create.roleLabel")}
            </label>
            <select
              id="cu-role"
              value={role}
              onChange={(e) => setRole(e.target.value as UserRole)}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              disabled={mutation.isPending}
            >
              <option value="user">{t("admin.users.create.roleUser")}</option>
              <option value="admin">{t("admin.users.create.roleAdmin")}</option>
            </select>
          </div>

          {apiError ? (
            <div
              role="alert"
              className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
            >
              {apiError}
            </div>
          ) : null}

          <div className="flex justify-end gap-2 border-t border-slate-100 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
              disabled={mutation.isPending}
            >
              {t("common.actions.cancel")}
            </button>
            <button
              type="submit"
              disabled={!valid || mutation.isPending}
              className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              {mutation.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              ) : null}
              {t("admin.users.create.submit")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
